/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomas Burian (burian@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import {RenderDocument, CameraAnimator as RenderCameraAnimator, InterpolatorsFactory, LinearInterpolator} from "js_renderer";
import {vec2, vec3} from "gl-matrix";
import IAnimatorModule from "./scene_animator_interface";
import Scene from "../../scene";
import ViewModule from "../view/scene_view";

export default
class AnimatorModule extends AbstractModule implements IAnimatorModule {

    protected static _typeName: string = "animator";

    private _animator: RenderCameraAnimator;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);
        this._type = AnimatorModule._typeName;

        this._animator = null;
    }

    public static getClassType(): string {
        return AnimatorModule._typeName;
    }

    public viewChanged() {
        //
    }

    public viewResized(aContentSize: vec2) {
        //
    }

    public init(aAnimator: RenderCameraAnimator/*, aViewMode: any*/) {
        this._animator = aAnimator;
    }

    public isInitialized(): boolean {
        return this._animator !== null;
    }

    public play() {
        if (this.isInitialized()) {
            this._animator.run();
        }
    }

    public pause() {
        if (this.isInitialized()) {
            this._animator.pause();
        }
    }

    public jumpTo(aPercentPosition: number) {
        if (this.isInitialized()) {
            this._animator.jumpTo(aPercentPosition);
        }
    }

    public destroy() {
        if (this.isInitialized()) {
            this.canvas.removeAnimator(this._animator);
        }
    }

    public setRepeat(aValue: boolean) {
        if (this.isInitialized()) {
            this._animator.setRepeat(aValue);
        }
    }

    public getCurrentAnimationCameraPosition(): vec3 {
        if (this.isInitialized()) {
            return this._animator.getCameraPosition();
        }
        return vec3.create();
    }

    public getCurrentAnimationCameraTarget(): vec3 {
        if (this._animator) {
            return this._animator.getCameraTarget();
        }
        return vec3.create();
    }

    public animateFile(aFrames: {position: vec3, target:vec3}[], aFps: number, aFov: number, anAnimationProgressCB: any, anAnimationFinishCB: any) {
        this._animator = new RenderCameraAnimator(this.sceneAPI);
        this._animator.setInterpolator(
            InterpolatorsFactory.create(LinearInterpolator.TypeName)
        );
        this._animator.setAnimationFrames(aFrames);
        this._animator.setFps(aFps);
        this._animator.setAnimationProgressCB(anAnimationProgressCB);
        this._animator.setAnimationFinishCB(anAnimationFinishCB);
        this._animator.setAutoDestruction(false);

        let camera = this.sceneAPI.viewControlsModule.getCamera();
        camera.setFieldOfView(aFov);

        this._animator.startAnimation();

        let sceneAnimator = new AnimatorModule(this.renderDocument);
        sceneAnimator.init(this._animator);

        //@ts-ignore
        this.canvas.addAnimatedObject(this.sceneAPI.viewControlsModule._controls, this._animator);
        sceneAnimator.pause();

        return sceneAnimator;
    }
}

Scene.registerSceneAPIModule(AnimatorModule);